import styled from "@emotion/styled";
import { ArrowRight } from "@mui/icons-material";
import {
  Breadcrumbs,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Eye, MagnifyingGlass } from "@phosphor-icons/react";
import { Button, TextField } from "components/UI";
import { useChurchStore } from "features/churches";
import {
  useExpenseStore,
  useExportRequisitions,
  useRequisitionsPaginated
} from "features/finances";
import { FILTER_OPERATIONS } from "features/members";
import { capitalize, startCase } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";
import { useAclStore } from "stores/aclStore";
import { statusColors } from "../../constants/statuses";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: ${({ bold }) => (bold ? 600 : 400)};
  cursor: pointer;
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SecondaryActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

const SearchIcon = styled(MagnifyingGlass)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

const Status = styled.div`
  border-radius: 2px;
  background: ${({ color }) => color};
  color: #ffffff;
  padding: 4px 8px;
  text-transform: capitalize;
  cursor: pointer;
`;

export const RequisitionTable = ({ onStatusAction }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [requisitionStatus, setRequisitionStatus] = useState("all");

  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const requisitionsQuery = useRequisitionsPaginated({
    churchId: selectedChurch?.id,
    page,
    pageSize,
    queries: {
      search: debouncedSearch,
      filter:
        requisitionStatus === "all"
          ? {
              is_voided: [
                {
                  operator: FILTER_OPERATIONS.EQUAL,
                  value: false
                }
              ]
            }
          : {
              is_voided: [
                {
                  operator: FILTER_OPERATIONS.EQUAL,
                  value: true
                }
              ]
            }
    }
  });
  const openRequisitionFlowModal = useExpenseStore(
    state => state.openRequisitionFlowModal
  );
  const role = useAclStore(state => state.role);

  const navigate = useNavigate();
  const auth = useAuthUser();
  const userId = auth()?.id;

  const { mutate: exportRequisitions, isLoading: isExporting } =
    useExportRequisitions();

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search]
  );

  const statusAction = requisition => {
    if (requisition.requisition_status.toLowerCase() === "not_approved") {
      onStatusAction(requisition, "approver");
      openRequisitionFlowModal();
    }

    if (requisition.requisition_status.toLowerCase() === "approved") {
      onStatusAction(requisition, "accountant");
      openRequisitionFlowModal();
    }
  };

  const navigateTo = requisition => {
    const isApprover = requisition?.approvers?.some(
      approver => approver.approver_id === userId
    );

    const isAccountant = role?.toLowerCase() === "accountant";

    if (
      (requisition.requisition_status.toLowerCase() === "not_approved" ||
        requisition.requisition_status.toLowerCase() === "pending") &&
      isApprover
    ) {
      return navigate(
        `/finances/temp/expense/requisition/${requisition.id}/approval`
      );
    }

    if (
      (requisition.requisition_status.toLowerCase() === "accountant" ||
        requisition.requisition_status.toLowerCase() === "scheduled") &&
      isAccountant
    ) {
      return navigate(
        `/finances/temp/expense/requisition/${requisition.id}/payment`
      );
    }

    navigate(`/finances/temp/expense/requisition/${requisition.id}`);
  };

  const columns = [
    {
      field: "id",
      headerName: "Requisition ID",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography sx={{ fontSize: "14px" }}>
              {row.transaction_number}
            </Typography>
            {row.is_voided && <Chip size="small" color="error" label="Void" />}
          </Stack>
        );
      }
    },
    {
      field: "requested_date",
      headerName: "Requested Date",
      flex: 1,
      renderCell: ({ row }) => {
        return <>{moment(row.requested_date).format("YYYY-MM-DD")}</>;
      }
    },
    {
      field: "name",
      headerName: "Requested By",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <>
            {row.user?.first_name} {row.user?.last_name}
          </>
        );
      }
    },
    {
      field: "amount",
      headerName: "Total Amount",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "reason",
      headerName: "Purpose",
      flex: 1
    },
    {
      field: "department",
      headerName: "Department",
      flex: 1,
      renderCell: ({ row }) => {
        return <>{row.team?.name}</>;
      }
    },
    {
      field: "due_date",
      headerName: "Due Date",
      flex: 1,
      renderCell: ({ row }) => {
        return <>{moment(row.due_date).format("YYYY-MM-DD")}</>;
      }
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Status
            color={statusColors[row.requisition_status.toUpperCase()]}
            onClick={() => statusAction(row)}
          >
            {startCase(capitalize(row.requisition_status))}
          </Status>
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <IconButton onClick={() => navigateTo(row)}>
            <Eye />
          </IconButton>
        );
      }
    }
  ];

  return (
    <Container>
      <Stack alignItems="flex-start" spacing={4}>
        <Header>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Breadcrumbs
              separator={<ArrowRight fontSize="small" />}
              aria-label="breadcrumb"
            >
              <Title onClick={() => navigate("/finances/temp/expense")}>
                Expense
              </Title>
              <Title bold>Requisition</Title>
            </Breadcrumbs>
            <Select
              value={requisitionStatus}
              onChange={e => setRequisitionStatus(e.target.value)}
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="void">Void</MenuItem>
            </Select>
          </Stack>
          <PrimaryActions>
            <Button
              onClick={() =>
                navigate(`/finances/temp/expense/requisition/request`)
              }
            >
              New Request
            </Button>
          </PrimaryActions>
        </Header>
        <SecondaryActions>
          <Stack direction="row" alignItems="center" spacing={2}>
            <TextField
              neutral={1}
              placeholder="Search requisition"
              register={() => {}}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={16} />
                  </InputAdornment>
                )
              }}
              autoComplete="off"
              onChange={e => setSearch(e.target.value)}
            />
            <Button
              onClick={() =>
                exportRequisitions({
                  churchId: selectedChurch?.id,
                  queries: {
                    search: debouncedSearch
                  }
                })
              }
              disabled={isExporting}
            >
              Export
            </Button>
          </Stack>
        </SecondaryActions>
      </Stack>
      <DataGrid
        autoHeight
        rows={requisitionsQuery.data?.result?.data || []}
        rowCount={requisitionsQuery.data?.result?.total || 0}
        loading={requisitionsQuery.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
      />
    </Container>
  );
};
